import { computed, makeObservable } from 'mobx';

import { FieldModel } from 'shared/models/form';
import { validateIsEmpty } from 'shared/entities/validator';
import { IRootStore } from 'shared/entities/store/rootStore';
import { LoadingStageModel } from 'shared/models/loadingStage';
import { apiUrls } from 'shared/entities/domain';
import {
  ChannelCommonServer,
  ChannelKind,
  CreateChannelError,
  mapCreateChannelErrorToMessage
} from 'shared/entities/channels';
import { CommonChannelModel } from 'shared/models/channel';

export class MAXChannelCreationStore {
  readonly kind: ChannelKind.MAX = ChannelKind.MAX;
  readonly token: FieldModel = new FieldModel<string>('', [validateIsEmpty]);
  readonly creationStage: LoadingStageModel = new LoadingStageModel();
  readonly rootStore: IRootStore;

  constructor({ rootStore }: { rootStore: IRootStore }) {
    this.rootStore = rootStore;

    makeObservable(this, {
      isError: computed
    });
  }

  get isError(): boolean {
    return this.token.isError;
  }

  validate(): boolean {
    this.token.validate();

    return this.isError;
  }

  reset(): void {
    this.token.reset();
  }

  create = async (): Promise<BaseResponse<{ channelId: string }>> => {
    const validationError = this.validate();

    if (validationError || this.creationStage.isLoading) {
      return {
        isError: true
      };
    }

    this.creationStage.loading();

    const response = await this.rootStore.networkStore.api<
      {
        channel: ChannelCommonServer;
      },
      CreateChannelError
    >(apiUrls.CHANNELS_MAX_CREATE, {
      method: 'POST',
      data: {
        token: this.token.value
      },
      errorsMap: mapCreateChannelErrorToMessage,
      showExpectedError: false
    });

    if (response.isError) {
      this.creationStage.error();

      if (
        response.data?.code &&
        Object.values(CreateChannelError).includes(response.data?.code)
      ) {
        this.token.setError(mapCreateChannelErrorToMessage(response.data.code));
      }

      return {
        isError: true
      };
    }

    const channel = CommonChannelModel.fromJson(
      response.data.channel,
      this.rootStore
    );
    this.rootStore.channelsStore.addCreatedChannel(channel);

    this.reset();
    this.creationStage.success();

    return {
      isError: false,
      data: {
        channelId: channel.id
      }
    };
  };
}
